import {
  useEffect, useMemo, useRef, useState,
} from 'react';

import { serverNow } from '@/common/time/serverNow';

/**
 * Authoritative per-question countdown from the server deadline.
 * Freezes while the exam is paused; does not advance locally during pause.
 */
export function useOralExamQuestionTimer(
  secondsPerQuestion: number,
  active: boolean,
  questionDeadline?: string | null,
  isPaused = false,
): { remainingSeconds: number; formatted: string } {
  const [tick, setTick] = useState(0);
  const frozenRemainingRef = useRef<number | null>(null);

  const remainingSeconds = useMemo(() => {
    if (!active || !questionDeadline || secondsPerQuestion <= 0) {
      return secondsPerQuestion;
    }
    const deadlineMs = new Date(questionDeadline).getTime();
    const rawRemaining = Math.max(0, Math.floor((deadlineMs - serverNow()) / 1000));
    const remaining = rawRemaining > secondsPerQuestion ? secondsPerQuestion : rawRemaining;
    if (isPaused) {
      if (frozenRemainingRef.current === null) {
        frozenRemainingRef.current = remaining;
      }
      return frozenRemainingRef.current;
    }
    frozenRemainingRef.current = null;
    return remaining;
    // `tick` forces re-computation each second while the clock is running.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, questionDeadline, secondsPerQuestion, isPaused, tick]);

  useEffect(() => {
    if (!active || !questionDeadline || isPaused) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [active, questionDeadline, isPaused]);

  useEffect(() => {
    if (!isPaused) {
      frozenRemainingRef.current = null;
    }
  }, [isPaused]);

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const formatted = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return { remainingSeconds, formatted };
}
